/**
 * Render a `Doc` as React elements.
 *
 * There are two ways to paint a Doc, and they must agree:
 *
 *   markup    Doc → serialize.ts   → HTML string   (saving, export, capture)
 *   elements  Doc → this module    → React tree    (editor canvas, Remotion)
 *
 * Both take every CSS declaration from `layer-css`, so pixels can't drift.
 * This module owns the other half of that contract — which element each layer
 * type becomes, and how they nest — so the two element consumers can't drift
 * from each other either:
 *
 *   editor canvas   interactive, hit-testable, text-editable
 *   Remotion        each animated layer wrapped in a frame-driven component
 *
 * Neither of those concerns lives here. Consumers inject them through
 * {@link DocRenderOptions}; the core stays a pure Doc → React function with no
 * knowledge of editing or of Remotion. It lives in this package rather than in
 * `@hightouch/lib` because lib deliberately holds no React dependency —
 * `layer-css` types its output as a plain string map for exactly that reason.
 */
import React, { type CSSProperties, type ReactNode } from "react";
import {
  type CssMap,
  cropImageCss,
  cssToReactStyle,
  docRootCss,
  layerCss,
} from "@hightouch/lib/ad-studio/html-editor/layer-css";
import type {
  Doc,
  ImageLayer,
  Layer,
  RectLayer,
  SvgLayer,
  TextLayer,
} from "@hightouch/lib/ad-studio/html-editor/model";
import { isFlexGroup } from "@hightouch/lib/ad-studio/html-editor/model";

/**
 * Class on every Doc root painted into a host page. Hosts that carry their own
 * stylesheet (the app) scope an `all: revert` reset to this class so global
 * rules such as `img { max-width: 100% }` cannot reframe layers sized wider
 * than the canvas. Inert where no stylesheet defines it (Lambda, the sandbox).
 */
export const AD_CSS_ISOLATION_CLASS = "ht-ad-canvas";

export interface DocRenderOptions {
  /**
   * Frame-specific content transform applied before a layer is rendered.
   * Used by HTML motion effects such as typewriter, draw and color-shift.
   */
  transformLayer?: (layer: Layer) => Layer;
  /**
   * Wrap a layer's rendered element. Remotion nests the animation components
   * that read `useCurrentFrame()` here; the editor passes nothing.
   */
  wrapLayer?: (layer: Layer, rendered: ReactNode) => ReactNode;
  /**
   * Extra DOM props merged onto a layer's root element — the editor's
   * `data-ht-id` / `data-slot` hit-testing attributes.
   */
  layerProps?: (layer: Layer) => Record<string, unknown> | undefined;
  /**
   * Style applied *before* the shared declarations, so the shared ones win.
   * Needed for defaults a source-declared value must be able to override —
   * the editor's text wrapping defaults, where listing them after would re-set
   * `text-wrap-mode` via the `white-space` shorthand and silently override a
   * source's `nowrap` headline.
   */
  layerStyleBefore?: (
    layer: Layer,
    inFlow: boolean,
  ) => CSSProperties | undefined;
  /**
   * Style applied *after* the shared declarations, so it wins. The editor's
   * deliberate deviations (unclipped glyphs, cursor and selection affordances)
   * arrive this way.
   */
  layerStyle?: (layer: Layer, inFlow: boolean) => CSSProperties | undefined;
  /**
   * Take over a layer's rendering entirely. Returning `undefined` falls back
   * to the standard element for that layer type. The editor uses this for the
   * `contentEditable` text layer, whose inner DOM React must not own.
   */
  renderLayer?: (layer: Layer, inFlow: boolean) => ReactNode | undefined;
  /**
   * Takes over a group's child traversal. Frame-driven consumers use this to
   * put a component boundary around each child, so one animated descendant
   * does not invalidate its static siblings.
   */
  renderChildren?: (layers: readonly Layer[], inFlow: boolean) => ReactNode;
}

/** `layer-css` is React-agnostic by design; this is the one cast that bridges it. */
export function reactStyle(css: CssMap): CSSProperties {
  return cssToReactStyle(css) as CSSProperties;
}

/**
 * The declarations for one layer: consumer defaults, then the shared
 * definition, then consumer overrides. Exported so a consumer taking over a
 * layer via {@link DocRenderOptions.renderLayer} can reproduce the exact same
 * style rather than reassembling it.
 */
export function layerStyleFor(
  layer: Layer,
  inFlow: boolean,
  options: DocRenderOptions = {},
): CSSProperties {
  return {
    ...options.layerStyleBefore?.(layer, inFlow),
    ...reactStyle(layerCss(layer, inFlow)),
    ...options.layerStyle?.(layer, inFlow),
  };
}

function propsFor(
  layer: Layer,
  options: DocRenderOptions,
): Record<string, unknown> {
  return options.layerProps?.(layer) ?? {};
}

export function renderLayer(
  layer: Layer,
  inFlow: boolean,
  options: DocRenderOptions = {},
): ReactNode {
  const renderedLayer = options.transformLayer?.(layer) ?? layer;
  const overridden = options.renderLayer?.(renderedLayer, inFlow);
  const rendered =
    overridden !== undefined
      ? overridden
      : renderStandard(renderedLayer, inFlow, options);
  return options.wrapLayer
    ? options.wrapLayer(renderedLayer, rendered)
    : rendered;
}

function renderStandard(
  layer: Layer,
  inFlow: boolean,
  options: DocRenderOptions,
): ReactNode {
  switch (layer.type) {
    case "rect":
      return renderRect(layer, inFlow, options);
    case "text":
      return renderText(layer, inFlow, options);
    case "image":
      return renderImage(layer, inFlow, options);
    case "svg":
      return renderSvg(layer, inFlow, options);
    case "group":
      return renderGroup(layer, inFlow, options);
  }
}

function renderRect(
  layer: RectLayer,
  inFlow: boolean,
  options: DocRenderOptions,
): ReactNode {
  return (
    <div
      {...propsFor(layer, options)}
      style={layerStyleFor(layer, inFlow, options)}
    />
  );
}

function renderText(
  layer: TextLayer,
  inFlow: boolean,
  options: DocRenderOptions,
): ReactNode {
  return (
    <div
      {...propsFor(layer, options)}
      style={layerStyleFor(layer, inFlow, options)}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: text layers store sanitized inline HTML so spans/strong/em survive editing
      dangerouslySetInnerHTML={{ __html: layer.content }}
    />
  );
}

function renderImage(
  layer: ImageLayer,
  inFlow: boolean,
  options: DocRenderOptions,
): ReactNode {
  // Cropped: the layer box is the visible frame (it clips); the image is drawn
  // at its own AR-locked box, offset within the frame. This is what keeps a
  // crop from distorting — the image box never takes the frame's aspect ratio.
  if (layer.crop) {
    return (
      <div
        {...propsFor(layer, options)}
        style={layerStyleFor(layer, inFlow, options)}
        // Inert in a video render; the editor relies on it to stop the
        // browser's native HTML5 image drag hijacking a move gesture.
        draggable={false}
      >
        <img
          alt=""
          // The resolved, loadable URL — this is a paint path, not a
          // persistence path, so it never uses the portable `adRef`.
          src={layer.src}
          style={reactStyle(cropImageCss(layer.crop, layer.objectFit))}
          draggable={false}
        />
      </div>
    );
  }
  return (
    <img
      {...propsFor(layer, options)}
      alt=""
      src={layer.src}
      style={layerStyleFor(layer, inFlow, options)}
      draggable={false}
    />
  );
}

function renderSvg(
  layer: SvgLayer,
  inFlow: boolean,
  options: DocRenderOptions,
): ReactNode {
  return (
    <svg
      {...propsFor(layer, options)}
      style={layerStyleFor(layer, inFlow, options)}
      viewBox={layer.viewBox || undefined}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: opaque user-supplied SVG markup
      dangerouslySetInnerHTML={{ __html: layer.innerMarkup }}
    />
  );
}

function renderGroup(
  layer: Layer & { type: "group" },
  inFlow: boolean,
  options: DocRenderOptions,
): ReactNode {
  const childrenInFlow = isFlexGroup(layer);
  return (
    <div
      {...propsFor(layer, options)}
      style={layerStyleFor(layer, inFlow, options)}
    >
      {options.renderChildren
        ? options.renderChildren(layer.children, childrenInFlow)
        : layer.children.map((child) => (
            <React.Fragment key={child.id}>
              {renderLayer(child, childrenInFlow, options)}
            </React.Fragment>
          ))}
    </div>
  );
}

/** The document's top-level layers, without the root container. */
export function renderDocLayers(
  doc: Doc,
  options: DocRenderOptions = {},
): ReactNode {
  return doc.layers.map((layer) => (
    <React.Fragment key={layer.id}>
      {renderLayer(layer, false, options)}
    </React.Fragment>
  ));
}

/** Style for the document's root container. */
export function docRootStyle(
  doc: Doc,
  size?: { width: number; height: number },
): CSSProperties {
  return reactStyle(docRootCss(doc, size));
}
